import React, { useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/css';
import appLabels from '../../utils/appLabels';
import formatString, { formatPrice } from '../../utils/formatString';
import OrderDate from './OrderDate';
import OrderAssetList from './OrderAssetList';
import ScheduledItemsList from '../ScheduledItems/ScheduledItemsList';
import PropTypes from 'prop-types';
import { invoke, get, has, map, size, find, filter, some, toNumber, add } from 'lodash';
import Modal from '../../common/Modal/Modal';
import AppLabels from '../../utils/appLabels';
import moment from 'moment';
import OrderInfoIcon from './OrderInfoIcon';
import AvailableSlotPreferences from '../AvailableSlotPreferences/AvailableSlotPreferences';
import { ItemsContext, ItemsContextType } from '../ScheduledItems/ItemsContext';
import { useApp } from '../../context/app-context';
import ExpandOrderButton from './ExpandOrderButton';
import Accordion from '../Accordion/Accordion';
import { AddOneTimeItems } from './OneTimeAddItems';
import { TipOptions, CustomTip, CustomTipMode } from './DriverTipComponent';
import { DriverTipType } from '../../utils/constants';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';
import updateDriverTipService from '../../service/update-driver-tip-service';
import { DriverTipType as DriverTipPayloadType } from '../../service/update-driver-tip-service';
import Loader from '../../common/Loading/loader';
import { SHOULD_INCLUDE_TIP } from '../../utils/utils';

const tileStyle = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const nextOrderTileStyle = css`
  width: 100%;
`;

const expandedTileStyle = css`
  width: 100% !important;
  max-width: initial;
`;

const dateSectionStyle = css`
  display: flex;
  flex-direction: row;
  padding: 0.75rem;
`;

const dateSectionExpandedStyle = css`
  background-color: #faf9f8;
`;

const itemTextStyle = css`
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.25rem;
`;

const detailsStyle = css`
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.125rem;
`;

const deliveryInstructionsStyle = css`
  font-size: 12px;
  font-weight: 400;
  color: #6e6e6d;
  line-height: 17.4px;
`;
const editableDateStyle = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
  padding: 0;
  text-align: start;
`;

const dateTextStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 0.75rem;
  flex: 1;

  & .addressLine {
    display: flex;
    gap: 0.5rem;
    align-items: center;

    @media (max-width: 641px) {
      align-items: flex-start;
      flex-direction: column;
      gap: initial;
    }
  }
`;

const midSectionStyle = css`
  padding: 0 1rem;
`;

const bottomSectionStyle = css`
  display: flex;
  justify-content: space-between;
  padding: 0 0.75rem 0.75rem;

  @media (max-width: 641px) {
    flex-direction: column-reverse;
  }
`;

const manageLinkStyle = css`
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-decoration-line: underline !important;
  color: #7b1a27 !important;
  cursor: pointer;
  flex: 1;
  margin-right: 0.3rem;
  background: white;
  border: none;
  text-align: start;
  padding: 0;
`;

const totalsContainerStyle = css`
  flex: 1;
  padding-left: 0.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: 641px) {
    padding-left: 0;
  }
`;

const subtotalsContainerStyle = css`
  @media (max-width: 425px) {
    & > .body-text {
      font-size: 14px;
    }
  }
`;

const totalsContainerStyleExpanded = css`
  padding: 1rem 1.5rem 0;

  @media (max-width: 641px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const totalsHeadingStyle = css`
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.463rem;
  padding-bottom: 0.625rem;
`;

const totalEstimatedSavingsPrice = css`
  color: #04633c;
  font-weight: 600;
`;

const estimatedSavingsStyle = css`
  display: flex;
  flex-direction: column;
  color: #04633c;

  & > div {
    display: flex;
    text-align: center;
    padding-left: 0.5rem;
    justify-content: space-between;
    margin-top: 0.5rem;

    @media (max-width: 425px) {
      & > .label {
        font-size: 14px;
      }
    }

    & > p {
      margin: 0;
      @media (max-width: 425px) {
        font-size: 14px;
      }
    }
  }

  & > .label {
    flex: 1;
  }
`;

const subTotalStyle = css`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;

  svg {
    margin-right: 0.2rem;
  }

  & > p {
    margin: 0;
  }

  & > .label {
    flex: 1;
  }
`;

const infoIconStyle = css`
  display: flex;
  height: 1rem;
`;

const svgStyle = css`
  svg {
    margin-left: 0.2rem;
  }
`;

const strokeStyle = css`
  height: 0.0625rem;
  background: #f1f0ee;
  margin: 0.75rem 0 0.5rem;
`;

const collapesedInfoIconStyle = css`
  margin-right: 0.2rem;
`;

const subTotalStyleExpanded = css`
  & > .amount {
    font-size: 1rem;

    @media (max-width: 641px) {
      font-size: 1rem;
    }

    @media (max-width: 425px) {
      font-size: 14px;
    }
  }

  & > .label {
    @media (max-width: 425px) {
      font-size: 14px;
    }
  }

  & > .original {
    color: #4b4b49;
    text-decoration: line-through;
    margin-right: 0.25rem;
  }
`;

const subTotalStyleCollapsed = css`
  font-size: 0.875rem;
  width: 100%;

  & .original {
    display: none;
  }

  & .next-order-amount-padding {
    margin-left: 0.3rem;
    margin-top: initial;
    margin-bottom: initial;
  }
`;

const termsStyle = css`
  white-space: break-spaces;
  flex-basis: 60%;
  padding: 1rem 0;
`;

const extraSmallText = css`
  font-size: 10px;
`;

const flexRow = css`
  display: flex;
  align-items: center;
`;

const slotsMessageContainer = css`
  margin-bottom: 1rem;
`;

const accordionIconStyle = css`
  display: flex;
  margin: 1px 0.5rem 0 0;
`;

const availableSlotsModalTitle = css`
  @media (max-width: 425px) {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const itemSummaryContainerStyle = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0 1rem;
`;
const itemSummaryStyle = css`
  font-weight: 600;
  color: #1f1e1e;
`;

const driverTipContainer = css`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  width: 420px;
  @media (max-width: 641px) {
    width: auto;
  }
`;
const oneDriverTipOptionContainer = css`
  display: flex;
  flex-direction: row-reverse;
  column-gap: 12px;
  width: 420px;
  @media (max-width: 641px) {
    width: auto;
  }
`;

const oneOptionNoTipStyle = css`
  @media (max-width: 641px) {
    flex: 1;
  }
`;

const driverTipHeadingContainer = css`
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;
`;

const driverTipHeading = css`
  display: flex;
  font-weight: 400;
  align-items: center;
  font-size: 1rem;
  @media (max-width: 641px) {
    font-size: 0.875rem;
  }
`;

const driverTipOptionsContainer = css`
  display: flex;
  gap: 10px;

  @media (max-width: 641px) {
    justify-content: space-between;
  }
`;

const customTipStyle = css`
  width: 50%;
  padding: 0;
`;

const oneDriverTipOptionCustomContainer = css`
  width: 100%;
  padding: 0;
  margin-bottom: 0.2rem;
`;

const tipOptionsNoTipButton = css`
  padding: 0;
`;

const oneTipOptionsNoTipButton = css`
  display: flex;
  width: inherit;
  @media (max-width: 641px) {
    width: 100%;
  }
`;

const infoIconWhiteStyle = css`
  background: #ffffff;
  border: none;
`;

const infoIconWhiteContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 5px 20px;

  p{
    width: 345px; 
    font-size: 16px; 
    font-weight: 400;
    line-height: 23.2px;
    @media (max-width: 641px) {
      width: fit-content;
    } 
  }

  button{
    width: 20.9375rem; 
    height: 3rem; 
    border: none;
    border-radius: 6.25rem; 
    background: #7b1a27;
    cursor: pointer;
    color: #ffffff;
    padding: 0.75rem 1.25rem; 
    margin-top: 0.5rem;
    font-size: 1rem; 
    font-weight: 500;
  }
} //TODO need to remove this additional braces
`;

const editDriverTipStyle = css`
  display: flex;
  font-size: 12px;
  font-weight: 400;
  margin-top: 0.5rem;
  p {
    margin-bottom: 5px;
    margin-left: 5px;
    color: #2d5a7a;
  }
`;

const tipPreferenceModalContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 5px 20px;
  width: 351px;

  p{
    font-size: 16px; 
    font-weight: 400;
    line-height: 23.2px; 
  }
  @media (max-width: 641px) {
    width: auto;
  }
}
`;

const tipConfirmationModalContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 5px 20px;

  p{
    width: 345px; 
    font-size: 16px; 
    font-weight: 400;
    line-height: 23.2px; 
  }
  button{
    width: 20.9375rem; 
    height: 3rem; 
    border: none;
    border-radius: 6.25rem; 
    background: #7b1a27;
    cursor: pointer;
    color: #ffffff;
    padding: 0.75rem 1.25rem; 
    margin-top: 0.5rem;
    font-size: 1rem; 
    font-weight: 500;
  }
}
`;

const tipPreferenceButtonContainer = css`
  display: flex;
  justify-content: center;
  @media (max-width: 641px) {
    display: flex;
    flex-direction: row;
    margin-top: 1.25rem;
  }
`;

const notTipSetButton = css`
  width: 185px;
  height: 48px;
  margin: 0 0.375rem;
  min-width: 6rem;
  min-height: 3rem;
  border-radius: 6.25rem;
  background: #ffffff;
  border: 0.063rem solid #7b1a27;
  color: #7b1a27;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.45rem;
  text-align: center;
  cursor: pointer;
  @media (max-width: 641px) {
    width: 161.5px;
    min-width: 96px;
    font-size: 16px;
  }
`;

const tipSetButton = css`
  width: 185px;
  height: 48px;
  margin: 0 0.375rem;
  min-width: 6rem;
  min-height: 3rem;
  border-radius: 6.25rem;
  background: #7b1a27;
  border: 0.063rem solid #7b1a27;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.45rem;
  text-align: center;
  cursor: pointer;
  @media (max-width: 641px) {
    width: 161.5px;
    min-width: 96px;
    font-size: 16px;
  }
`;

const driverTipAmountStyle = css`
  font-weight: 600;
  font-size: 1rem;
  line-height: 20.3px;
  color: #1f1e1e;
  @media (max-width: 641px) {
    font-size: 0.875rem;
  }
`;

const OneTimeAddsIcon = () => (
  <div className={accordionIconStyle}>
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.806 11.527a6.167 6.167 0 1 1 8.721-8.72 6.167 6.167 0 0 1-8.72 8.72ZM2.1 2.1a7.167 7.167 0 1 0 10.135 10.135A7.167 7.167 0 0 0 2.1 2.1Zm1.06 4.568a.5.5 0 1 0 0 1h3.508v3.507a.5.5 0 0 0 1 0V7.667h3.507a.5.5 0 1 0 0-1H7.667V3.16a.5.5 0 1 0-1 0v3.507H3.16Z"
        fill="#1F1E1E"
      />
    </svg>
  </div>
);
const SubscribedIcon = () => (
  <div className={accordionIconStyle}>
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
      <path
        d="M13.34 7.154C13.167 1.326 5.232.176 2.896 5.234a.5.5 0 0 1-.908-.42C4.63-.9 13.513.021 14.295 6.534l.556-.98a.5.5 0 1 1 .87.494l-1.537 2.705a.5.5 0 0 1-.831.058l-1.898-2.465a.5.5 0 0 1 .792-.61l1.092 1.418ZM2.647 7.186a.5.5 0 0 0-.83.058L.278 9.949a.5.5 0 1 0 .87.494l.556-.98c.782 6.512 9.666 7.435 12.306 1.72a.5.5 0 1 0-.908-.42c-2.335 5.058-10.27 3.908-10.442-1.92l1.092 1.418a.5.5 0 1 0 .793-.61L2.647 7.185Z"
        fill="#1F1E1E"
      />
    </svg>
  </div>
);

const blueInfoIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.25 8C0.25 3.71979 3.71979 0.25 8 0.25C12.2802 0.25 15.75 3.71979 15.75 8C15.75 12.2802 12.2802 15.75 8 15.75C3.71979 15.75 0.25 12.2802 0.25 8ZM8.67949 4.59999C8.67949 4.22472 8.37527 3.9205 8 3.9205C7.62473 3.9205 7.32051 4.22472 7.32051 4.59999V5.13332C7.32051 5.50859 7.62473 5.81281 8 5.81281C8.37527 5.81281 8.67949 5.50859 8.67949 5.13332V4.59999ZM8.67949 7.43349C8.67949 7.05821 8.37527 6.754 8 6.754C7.62473 6.754 7.32051 7.05821 7.32051 7.43349V11.4C7.32051 11.7753 7.62473 12.0795 8 12.0795C8.37527 12.0795 8.67949 11.7753 8.67949 11.4V7.43349Z"
      fill="#2D5A7A"
    />
  </svg>
);

const InfoIconWhite = ({analyticsEvents}) => {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div>
      <button className={infoIconWhiteStyle} onClick={() => { setShowInfo(true); analyticsEvents('support-your-driver')}} data-testid="tip-info-button">
        <svg
          width="16"
          height="16"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden={true}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.0004 1.5394C5.77551 1.5394 1.53988 5.77502 1.53988 10.9999C1.53988 16.2248 5.77551 20.4605 11.0004 20.4605C16.2253 20.4605 20.4609 16.2248 20.4609 10.9999C20.4609 5.77502 16.2253 1.5394 11.0004 1.5394ZM0.460938 10.9999C0.460938 5.17913 5.17962 0.460449 11.0004 0.460449C16.8212 0.460449 21.5399 5.17913 21.5399 10.9999C21.5399 16.8207 16.8212 21.5394 11.0004 21.5394C5.17962 21.5394 0.460938 16.8207 0.460938 10.9999Z"
            fill="#2A2928"
          />
          <path
            d="M11.5879 16.235C11.4275 16.4116 11.2233 16.4999 10.9754 16.4999C10.7275 16.4999 10.516 16.4116 10.341 16.235C10.1806 16.0583 10.1004 15.8522 10.1004 15.6167V9.58152C10.1004 9.346 10.1806 9.13992 10.341 8.96328C10.516 8.78664 10.7275 8.69832 10.9754 8.69832C11.2233 8.69832 11.4275 8.78664 11.5879 8.96328C11.7629 9.13992 11.8504 9.346 11.8504 9.58152V15.6167C11.8504 15.8522 11.7629 16.0583 11.5879 16.235ZM11.5879 7.1196C11.4275 7.28152 11.2233 7.36248 10.9754 7.36248C10.7275 7.36248 10.516 7.28152 10.341 7.1196C10.1806 6.94296 10.1004 6.72952 10.1004 6.47928V5.88312C10.1004 5.6476 10.1806 5.44152 10.341 5.26488C10.516 5.08824 10.7275 4.99992 10.9754 4.99992C11.2233 4.99992 11.4275 5.08824 11.5879 5.26488C11.7629 5.44152 11.8504 5.6476 11.8504 5.88312V6.47928C11.8504 6.72952 11.7629 6.94296 11.5879 7.1196Z"
            fill="#2A2928"
          />
        </svg>
      </button>
      <Modal
        titleText="Support Your Driver with a Tip"
        titleTextCenter={true}
        show={showInfo}
        fillScreen={true}
        halfHeightMobileModalPerc={'55'}
        showBottomTransitionModal={true}
        showStrokeLines={false}
        modalOverflowValue="hidden"
        overFlowBodyHidden={true}
        isDriverTipInfoModal={true}
        onClose={() => setShowInfo(false)}
      >
        <div className={infoIconWhiteContainer}>
          <p>{appLabels.PendingOrderContent.driverTipModalText1}</p>
          <p>{appLabels.PendingOrderContent.driverTipModalText2}</p>
          <button
            onClick={() => {
              setShowInfo(false);
            }}
            type="button"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export enum MODAL_CTA {
  YES = 'Yes',
  NO = 'No',
}

const filterDriverTipOptions = (driverTipOptions, orderTotal) => {
  let showShowCustomTip = false;
  let customTipValue = 99.99;
  let hasValidSelectedTip = false;

  let noTipOption = find(driverTipOptions, (option) => get(option, 'tipPercentage', '') === DriverTipType.NOTIP);

  // Check if noTip was originally selected
  const hasNoTipSelected = noTipOption ? get(noTipOption, 'tipSelected', false) : false;

  // Check if custom tip was originally selected
  const hasOtherSelected = some(driverTipOptions, (option) => {
    const tipPercentage = get(option, 'tipPercentage', '');
    const tipSelected = get(option, 'tipSelected', false);

    return tipSelected && tipPercentage === DriverTipType.PREFERENCES_OTHER;
  });

  // Filter out 5%, 10%, 15% if tipAmount < 2 OR  tipAmount > 99.99
  const filteredOptions = filter(driverTipOptions, (option) => {
    const tipPercentage = get(option, 'tipPercentage', '');
    const isStandardTip = tipPercentage === '5' || tipPercentage === '10' || tipPercentage === '15';

    // Remove if 5%, 10%, or 15% AND tipRoundedAmount < 2
    if (isStandardTip && get(option, 'tipRoundedAmount', 0) < 2) {
      return false;
    }

    // Remove if 5%, 10%, or 15% AND tipRoundedAmount > 99.99
    if (isStandardTip && get(option, 'tipRoundedAmount', 0) > 99.99) {
      showShowCustomTip = true;
      return false;
    }

    return true;
  });

  // Check if any valid tip option below 99.99 was already selected
  const validTipOptionSelected = find(
    filteredOptions,
    (option) =>
      (get(option, 'tipPercentage') === '5' ||
        get(option, 'tipPercentage') === '10' ||
        get(option, 'tipPercentage') === '15') &&
      get(option, 'tipSelected'),
  );
  // If a valid tip below 99.99 is selected, keep it preselected
  if (validTipOptionSelected) {
    hasValidSelectedTip = true;
  }

  let updatedOptions = [...filteredOptions];

  //If all standard tips were below $2. Then select noTip, but do not add a new one.
  if (!hasValidSelectedTip && !showShowCustomTip && !hasOtherSelected) {
    if (noTipOption) {
      noTipOption.tipSelected = true;
    }
  } else if (hasNoTipSelected) {
    //If noTip was selected, we ensure it stays selected
    noTipOption.tipSelected = true;
  }

  //If all standard tip is removed due to exceeding $99.99, and & preselect Custom Tip
  if (showShowCustomTip && !hasValidSelectedTip && !hasOtherSelected) {
    updatedOptions = filter(
      updatedOptions,
      (option) => get(option, 'tipPercentage', '') !== DriverTipType.PREFERENCES_OTHER,
    );
    updatedOptions.push({
      tipPercentage: DriverTipType.PREFERENCES_OTHER,
      tipAmount: customTipValue,
      tipSelected: true,
      tipRoundedAmount: customTipValue,
    });
  }

  // If custom tip amount is more than order total , set the tip as 15% for the  upcoming order
  let hasCustomTipExceeded = false;
  updatedOptions = map(updatedOptions, (option) => {
    const tipPercentage = get(option, 'tipPercentage', '');
    const tipRoundedAmount = get(option, 'tipRoundedAmount', 0);
    const tipSelected = get(option, 'tipSelected', false);

    if (tipSelected && tipPercentage === DriverTipType.PREFERENCES_OTHER && tipRoundedAmount > orderTotal) {
      hasCustomTipExceeded = true;
      return { ...option, tipSelected: false };
    }
    return option;
  });

  if (hasCustomTipExceeded) {
    const selectFifteenPercentTip = find(updatedOptions, (option) => {
      return get(option, 'tipPercentage', '') === '15';
    });

    if (selectFifteenPercentTip) {
      selectFifteenPercentTip.tipSelected = true;
    } else if (noTipOption) {
      noTipOption.tipSelected = true;
    }
  }

  return updatedOptions;
};

const PendingOrderContent = (props) => {
  const { appData, showToast } = useApp();
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const upcomingOrders = get(props, 'upcomingOrders');
  const userPreferences = get(props, 'preferences');
  const callbacks = get(props, 'callbacks');
  const orderStatusMessage = get(props, 'orderStatusMessage');
  const hideManageLink = get(props, 'hideManageLink', false);
  const hideOrderStatusMessage = get(props, 'hideOrderStatusMessage', false);
  const hasRestrictedItems = get(upcomingOrders, 'hasRestrictedItems', false);
  const showRescheduleLink = get(props, 'showRescheduleLink', true);
  const [expanded, setExpanded] = useState(get(props, 'expanded', false));
  const [timeSlotModal, setTimeSlotModal] = useState(false);
  const [searchToken, setSearchToken] = useState('');
  const [loading, setLoading] = useState(false);
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;
  const { subscribe, unsubscribe } = useApp();
  const order = get(upcomingOrders, 'upcomingOrder');
  const cardSavings = get(order, 'cardSavings', []);
  const nextOrderTotal = get(order, 'estimatedSubTotal', 0);
  const isOneTimeAddsAllowed = get(upcomingOrders, 'isOneTimeAddsAllowed', false);
  const addressLine1 = get(upcomingOrders, 'fulfillment.address.addressLine1', '');
  const addressLine2 = get(upcomingOrders, 'fulfillment.address.addressLine2', '');
  const fulfillmentAddress = addressLine2 ? `${addressLine1} ${addressLine2}` : addressLine1;
  const fulfillmentStartTime: string = get(upcomingOrders, 'fulfillment.slotStart', 'null');
  const fulfillmentEndTime: string = get(upcomingOrders, 'fulfillment.slotEnd', 'null');
  const isSlotAvailable = fulfillmentStartTime === 'null' && fulfillmentEndTime === 'null';
  const notes = get(userPreferences, 'addresses[0].notes', []);
  const deliveryInstructionsNote = get(find(notes, { type: 'DELIVERY_INSTRUCTIONS' }), 'value', '');
  const deliveryTypeValue = get(find(notes, { type: 'DELIVERY_TYPE' }), 'value', '');
  const deliveryTypeText =
    deliveryTypeValue === 'ATTENDED' || hasRestrictedItems
      ? get(appLabels, 'PreferenceTab.handItToMeText', '')
      : get(appLabels, 'PreferenceTab.leaveAtDoorText', '');

  const driverTipInfo = get(upcomingOrders, 'driverTipInfo', {});

  const driverTipOptions = useMemo(() => get(driverTipInfo, 'driverTipOptions', []), [driverTipInfo, nextOrderTotal]);

  const selectedTipOption = useMemo(
    () => find(driverTipOptions, (option) => get(option, 'tipSelected') === true),
    [driverTipOptions],
  );

  const [driverTipOptionSelected, setDriverTipOptionSelected] = useState('');
  const [driverTipRoundedAmountSelected, setDriverTipRoundedAmountSelected] = useState(0);
  const [showDriverTipModal, setShowDriverTipModal] = useState(false);

  const tipOptionsList = useMemo(() => {
    return filter(driverTipOptions, (option) => {
      const tipPercentage = get(option, 'tipPercentage', '');
      return ['5', '10', '15', DriverTipType.NOTIP].includes(tipPercentage);
    });
  }, [driverTipOptions]);

  const customOptionsList = useMemo(() => {
    return filter(driverTipOptions, (option) => get(option, 'tipPercentage', '') === DriverTipType.PREFERENCES_OTHER);
  }, [driverTipOptions]);

  const [isCustomTipSelected, setIsCustomTipSelected] = useState(false);
  const [driverCustomTipValue, setDriverCustomTipValue] = useState('');
  const [isDriverTipPreferenceSet, setIsDriverTipPreferenceSet] = useState(false);
  const [showTipConfirmationModal, setShowTipConfirmationModal] = useState(false);
  const [customTipEditMode, setCustomTipEditMode] = useState(false);
  const [hasCustomTipError, setHasCustomTipError] = useState(false);
  const [customTipModeType, setCustomTipModeType] = useState('');

  const itemsText = formatString(appLabels.PendingOrderContent.itemsText, {
    numberOfItems: get(order, 'numberOfItems', 0),
    multiItem: get(order, 'numberOfItems') > 1 ? 's' : '',
    deliveryType: get(upcomingOrders, 'fulfillment.fulfillmentType') === 'DUG' ? 'pickup' : 'delivery',
  });

  const isDelivery = get(upcomingOrders, 'fulfillment.fulfillmentType', '').toUpperCase() !== 'DUG';

  const oneTimeOrderItemsCount = get(order, 'oneTimeOrderItems', [])
    .map((item: { quantity: string }) => +item?.quantity)
    .reduce((a, b) => a + b, 0);
  const subscribedItemsCount = get(order, 'orderItems', [])
    .map((item: { quantity: string }) => +item?.quantity)
    .reduce((a, b) => a + b, 0);

  const clearResults = () => {
    setSearchToken('');
  };

  const expandProductList = () => {
    invoke(callbacks, 'setFillWidth', true);
    setExpanded(true);
    analyticsEvents('view-manage-order');
  };

  const handleOrderSkip = () => {
    invoke(callbacks, 'setFillWidth', false);
    setExpanded(false);
  };

  const updateDriverTip = async (buttonType: MODAL_CTA | null, driverTipOptionSelected: any) => {
    setShowDriverTipModal(false);
    setLoading(true);
    try {
      const isNoTip = driverTipOptionSelected === DriverTipType.NOTIP;
      const isCustomTip = driverTipOptionSelected === DriverTipType.PREFERENCES_OTHER;
      const preferences = buttonType === MODAL_CTA.YES;

      let payload: DriverTipPayloadType = {
        tipPercentage: driverTipOptionSelected,
        preferences,
      };

      if (isNoTip) {
        payload = { ...payload, tipAmount: 0.0 };
      } else if (isCustomTip) {
        payload = { ...payload, tipAmount: toNumber(driverCustomTipValue) };
      }

      const response = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        updateDriverTipService(appData.environmentVariables, user, payload),
      );

      if (
        (get(response, 'data') && get(response.data, 'type') !== 'SUCCESS') ||
        get(response.data, 'statusCode') !== 200
      ) {
        throw new Error(get(response, 'message', ''));
      }

      const tipPercentage = get(payload, 'tipPercentage', null);
      const tipSelectedPreference = get(payload, 'preferences', null);
      const tipAmount = get(payload, 'tipAmount', 0);

      if (tipPercentage === DriverTipType.NOTIP) {
        if (tipSelectedPreference) {
          showToast(
            'info',
            formatString(appLabels.PendingOrderContent.updateNoTipSuccessForAllOrdersResponse, {
              tipPercentage: 'No Tip',
            }),
          );
        } else {
          showToast(
            'info',
            formatString(appLabels.PendingOrderContent.updateNoTipSuccessForUpcomingOrderResponse, {
              tipPercentage: 'No Tip',
            }),
          );
        }
      } else if (tipPercentage === DriverTipType.PREFERENCES_OTHER) {
        if (tipSelectedPreference) {
          showToast(
            'info',
            formatString(appLabels.PendingOrderContent.updateCustomTipSuccessForAllOrdersResponse, {
              tipPercentage: tipAmount,
            }),
          );
        } else {
          showToast(
            'info',
            formatString(appLabels.PendingOrderContent.updateCustomTipSuccessForUpcomingOrderResponse, {
              tipPercentage: tipAmount,
            }),
          );
        }
      } else {
        if (tipSelectedPreference) {
          showToast(
            'info',
            formatString(appLabels.PendingOrderContent.updateTipSuccessForAllOrdersResponse, {
              tipPercentage: tipPercentage,
            }),
          );
        } else {
          showToast(
            'info',
            formatString(appLabels.PendingOrderContent.updateTipSuccessForUpcomingOrderResponse, {
              tipPercentage: tipPercentage,
            }),
          );
        }
      }

      await invoke(currentItemsContext, 'fetchUpcomingOrdersInfo');
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showToast('error', appLabels.PendingOrderContent.updateTipErrorMessage);
    }
  };

  const customTipHandler = (value) => {
    const orderTotal = get(order, 'estimatedSubTotal', 0);
    const twentyPercentOfOrderTotal = orderTotal * 0.2;
    if (value > twentyPercentOfOrderTotal) {
      //launch warning modal and once he clicks got it button call updateDriverTip
      setShowTipConfirmationModal(true);
    } else {
      setShowDriverTipModal(true);
    }
  };

  const handleTipConfirmationModalClose = () => {
    setCustomTipEditMode(true);
    setShowTipConfirmationModal(false);
  };

  useEffect(() => {
    subscribe('skipOrder', handleOrderSkip);

    return () => {
      unsubscribe('skipOrder', handleOrderSkip);
    };
  }, []);

  useEffect(() => {
    setDriverTipOptionSelected(get(selectedTipOption, 'tipPercentage', ''));
    if (
      get(selectedTipOption, 'tipPercentage', '') !== DriverTipType.PREFERENCES_OTHER &&
      driverTipOptionSelected === DriverTipType.PREFERENCES_OTHER
    ) {
      setDriverCustomTipValue('');
      setIsCustomTipSelected(false);
    } else if (get(selectedTipOption, 'tipPercentage', '') === DriverTipType.PREFERENCES_OTHER) {
      setDriverTipOptionSelected(DriverTipType.PREFERENCES_OTHER); //set custom tip
      setDriverCustomTipValue(get(selectedTipOption, 'tipAmount', ''));
      setIsCustomTipSelected(true);
    }
  }, [selectedTipOption]);

  const customTipKey = useMemo(() => driverCustomTipValue, [driverCustomTipValue]);

  useEffect(() => {
    if (driverTipOptionSelected) return;
    if (!selectedTipOption) return;

    const tipPercentage = get(selectedTipOption, 'tipPercentage', '');
    const tipAmount = get(selectedTipOption, 'tipAmount', '');
    const selectedTipRoundedAmount = get(selectedTipOption, 'tipRoundedAmount', '');

    if (tipPercentage === DriverTipType.NOTIP) {
      setDriverTipOptionSelected(tipPercentage); //set noTip
      setDriverCustomTipValue(''); //clear custom tip value
    } else if (tipPercentage === DriverTipType.PREFERENCES_OTHER) {
      setDriverTipOptionSelected(DriverTipType.PREFERENCES_OTHER); //set custom tip
      setDriverCustomTipValue(tipAmount); //set custom tip value
      setIsCustomTipSelected(true); // by default is custom tip is false , so based on the condition we are setting it to true
    } else {
      setDriverTipOptionSelected(tipPercentage); //set numeric tip
      setDriverCustomTipValue(''); //clear custom tip value
      setDriverTipRoundedAmountSelected(formatPrice(selectedTipRoundedAmount));
    }
  }, [driverTipOptionSelected, driverTipOptions]);

  const analyticsEvents = (caller, payload?) => {
    try {
      if (window['AB']?.DATALAYER) {
        switch(caller) {
          case 'change-date-time-view':
            window['AB'].DATALAYER.setModalViewEvents(`ar:${payload ? '' : 'collapsed-state-'}change-date-time|view`);
            break;
          case 'view-manage-order':
            window['AB'].DATALAYER.setLinkClickEvents('ar:subscribe-schedule-and-save|view-manage-order');
            break;
          case 'set-driver-pref':
            window['AB'].DATALAYER.setModalClickEvents(`set-your-tip-prf-${window['AB'].DATALAYER.sanitizeAnalyticsText(payload.driverTipOptionSelected)}|${payload.option}`, null, true);
            break;
          case 'support-your-driver':
            window['AB'].DATALAYER.setModalViewEvents('support-your-driver-info|view');
            break;
          case 'set-your-tip-prf-view':
            window['AB'].DATALAYER.setModalViewEvents(`set-your-tip-prf-${payload}|view`);
            break;
          default:
            break;
        }
      }
      if (window['digitalData']) {
        window['digitalData'].pageFlags = window['digitalData'].pageFlags || {};
        window['digitalData'].pageFlags.isSnSItemsListExpanded = expanded;
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const onConfirm = () => {
    invoke(currentItemsContext, 'fetchUpcomingOrdersInfo');
    setTimeSlotModal(false);
  };

  const subtotalLabel = formatString(appLabels.PendingOrderContent.estimatedTextLabel, {
    subtotal: get(order, 'discountedEstSubTotal'),
  });

  const estimatedSubTotalItems = formatString(appLabels.FutureOrderContent.estimatedSubTotalItems, {
    totalItems: get(order, 'numberOfItems', 0),
    item: get(order, 'numberOfItems', 0) > 1 ? 'items' : 'item',
  });

  const _expanded = has(props, 'expanded') ? get(props, 'expanded') : expanded;

  const refreshNextOrder = async () => {
    await invoke(currentItemsContext, 'fetchItemsInfo');
    //expand the accordion
    setExpanded(true);
  };

  const calculateFinalTotal = (total, driverTipOptionSelected, shouldIncludeTip = true) => {
    // Extract selected tip amount (default to 0 if `noTip` is selected)
    const selectedTipAmount =
      get(driverTipOptionSelected, 'tipPercentage', '') !== DriverTipType.NOTIP
        ? toNumber(get(driverTipOptionSelected, 'tipRoundedAmount', 0))
        : 0;

    // If shouldIncludeTip is false, return only the estimatedSubTotal
    return shouldIncludeTip ? add(toNumber(total), selectedTipAmount, 0).toFixed(2) : total;
  };

  return (
    <div
      data-testid="pending-order-content"
      className={`${tileStyle} ${nextOrderTileStyle} ${expanded ? expandedTileStyle : ''}`}
    >
      {loading && <Loader useOverlay />}
      <div className={`${dateSectionStyle} ${expanded ? dateSectionExpandedStyle : ''}`}>
        <OrderDate orderDate={get(upcomingOrders, 'upcomingOrder.orderDate')} />
        <div className={dateTextStyle}>
          <div className={itemTextStyle}>{itemsText}</div>
          <div className="addressLine">
            <div>
              <div
                className={detailsStyle}
              >{`${fulfillmentAddress}${!isSlotAvailable ? `, ${fulfillmentStartTime} - ${fulfillmentEndTime}` : ''}`}</div>
              {isDelivery && (
                <div className={deliveryInstructionsStyle}>
                  {deliveryTypeText} {deliveryInstructionsNote && `: ${deliveryInstructionsNote}`}
                </div>
              )}
              {showRescheduleLink ? (
                <button
                  data-testid="reschedule-order"
                  className={`${editableDateStyle} body-text-sm`}
                  onClick={() => {
                    setTimeSlotModal(true);
                    analyticsEvents('change-date-time-view', expanded);
                  }}
                  aria-label={'Need it sooner order'}
                  aria-labelledby={'Need it sooner upcoming order'}
                >
                  <span className="link-sm">Need it sooner</span>
                </button>
              ) : null}
            </div>
          </div>

          {!hideOrderStatusMessage && (
            <span className={extraSmallText}>{orderStatusMessage ? orderStatusMessage : 'Order is preparing.'}</span>
          )}
          <Modal
            titleText={formatString(appLabels.PendingOrderContent.availableSlotsTitle, {
              startTime: moment(get(upcomingOrders, 'fulfillment.slotStart'), 'hh:mm A').format('hA'),
              endTime: moment(get(upcomingOrders, 'fulfillment.slotEnd'), 'hh:mm A').format('hA'),
              deliveryType: get(upcomingOrders, 'fulfillment.fulfillmentType') === 'DUG' ? 'pickup' : 'delivery',
              date: moment(get(order, 'orderDate'), 'YYYY-M-D').format('MMM D'),
            })}
            description=""
            onClose={() => setTimeSlotModal(false)}
            show={timeSlotModal}
            fillScreen={true}
            isAvailableSlotsModal={true}
            availableSlotsModalTitleClass={availableSlotsModalTitle}
          >
            <AvailableSlotPreferences
              nextOrderData={upcomingOrders}
              onConfirm={onConfirm}
              onError={() => setTimeSlotModal(false)}
            />
          </Modal>
        </div>
        {!hideManageLink && (
          <ExpandOrderButton
            expanded={_expanded}
            onChange={(value) => {
              setExpanded(value);
              invoke(props, 'onExpand', value);
            }}
          />
        )}
      </div>
      {isOneTimeAddsAllowed && expanded && !hideManageLink && (
        <AddOneTimeItems expanded={expanded} upcomingOrders={upcomingOrders} refreshNextOrder={refreshNextOrder} />
      )}
      {expanded && (
        <div className={itemSummaryContainerStyle}>
          <div className={itemSummaryStyle}>{appLabels.PendingOrderContent.ItemSummaryText}</div>
          <div className={itemSummaryStyle}>
            {appLabels.PendingOrderContent.estimatedTextExpanded} $
            {calculateFinalTotal(get(order, 'discountedEstSubTotal', 0), selectedTipOption, SHOULD_INCLUDE_TIP)}
          </div>
        </div>
      )}

      <div className={midSectionStyle}>
        {expanded ? (
          <div>
            {get(order, 'oneTimeOrderItems.length', 0) > 0 ? (
              <Accordion
                open={true}
                icon={<OneTimeAddsIcon />}
                className={css`
                  margin-bottom: 1rem;
                `}
                title={`One time adds ·  ${oneTimeOrderItemsCount} ${oneTimeOrderItemsCount > 1 ? 'items' : 'item'}`}
              >
                <ScheduledItemsList
                  showDeleteIcon={true}
                  showRemoveItemModal={true}
                  isOneTimeAddList={true}
                  cancellationReasons={get(upcomingOrders, 'cancellationReasons')}
                  items={get(order, 'oneTimeOrderItems')}
                  showSkipOnceLink={false}
                  showHeaders={false}
                  displayCancelButton={false}
                  showEditSubstitution={'UPCOMING_ORDER'}
                  showSubstitutionButton={true}
                  substitutionValue={get(upcomingOrders, 'oneTimeOrderItems')}
                  quantityResetOnError={true}
                  orderType="upcoming"
                />
              </Accordion>
            ) : null}
            {get(order, 'orderItems.length', 0) > 0 ? (
              <Accordion
                open={true}
                icon={<SubscribedIcon />}
                title={`Subscribed ·  ${subscribedItemsCount} ${subscribedItemsCount > 1 ? 'items' : 'item'}`}
              >
                <ScheduledItemsList
                  showDeleteIcon={true}
                  cancellationReasons={get(upcomingOrders, 'cancellationReasons')}
                  items={get(order, 'orderItems')}
                  showSkipOnceLink={true}
                  showSubstitutionButton={true}
                  showHeaders={false}
                  showEditSubstitution={'UPCOMING_ORDER'}
                  quantityResetOnError={true}
                  orderType="upcoming"
                />
              </Accordion>
            ) : null}
          </div>
        ) : (
          <OrderAssetList products={[...get(order, 'orderItems', []), ...get(order, 'oneTimeOrderItems', [])]} />
        )}
      </div>

      <div className={bottomSectionStyle}>
        {expanded ? (
          <>
            <div className={termsStyle} data-testid="terms">
              <span className="body-text-xs">
                {'The actual amount you will be charged for your order may be different from the Estimated Subtotal. The Estimated Subtotal does not include clipped coupons, “Buy One Get One” deals, the final cost of items sold by weight, or taxes/deposits/other fees. These adjustments will appear on your final receipt at delivery/pickup. The Estimated Total may also include discounts that are expired or not applicable when we prepare and process your order for final payment. Review Estimated Subtotal information Icon for details.\n' +
                  '\n' +
                  'Schedule & Save items receive a monetary discount off eligible items after other applicable offers have been applied. Certain products, such as dairy and alcohol, may be excluded from this discount. This is normally due to applicable federal and/or state regulations and restrictions.'}
              </span>
            </div>

            <div className={`${totalsContainerStyle} ${totalsContainerStyleExpanded}`}>
              <div className={totalsHeadingStyle}>Order summary</div>
              <div className={expanded ? subtotalsContainerStyle : ''}>
                <div className={`${subTotalStyle} ${expanded ? subTotalStyleExpanded : subTotalStyleCollapsed}`}>
                  <div className={`body-text label`}>
                    <div className={flexRow}>{estimatedSubTotalItems}</div>
                  </div>
                  <p className={`body-text amount`}>${get(order, 'estimatedSubTotal', '0.00')}</p>
                </div>
                {get(order, 'totalCardSavings') && (
                  <div className={`${subTotalStyle} ${expanded ? subTotalStyleExpanded : subTotalStyleCollapsed}`}>
                    <div className={`body-text label`}>
                      <div className={flexRow}>{appLabels.FutureOrderContent.estimatedSavings}</div>
                    </div>
                    <p className={`${totalEstimatedSavingsPrice} body-text amount`}>
                      -${get(order, 'totalCardSavings', '0.00')}
                    </p>
                  </div>
                )}

                <div className={estimatedSavingsStyle}>
                  {cardSavings.map(({ savingsCategoryName, savingsAmount = 0 }) => (
                    <div>
                      <div className="body-text label">{savingsCategoryName}</div>
                      <p className="body-text amount">-${formatPrice(savingsAmount)}</p>
                    </div>
                  ))}
                </div>
                {!!size(driverTipOptions) && (
                  <>
                    <div className={strokeStyle} />
                    <div className={driverTipHeadingContainer}>
                      <div className={driverTipHeading}>
                        Driver tip (optional)
                        <InfoIconWhite analyticsEvents={analyticsEvents}/>
                      </div>
                      <div className={driverTipAmountStyle}>
                        {get(selectedTipOption, 'tipRoundedAmount', 0) > 0 &&
                          (isCustomTipSelected
                            ? `$${formatPrice(Number(get(selectedTipOption, 'tipRoundedAmount', 0)))}`
                            : `$${formatPrice(get(selectedTipOption, 'tipRoundedAmount', 0))}`)}
                      </div>
                    </div>
                    <div className={tipOptionsList.length !== 1 ? driverTipContainer : oneDriverTipOptionContainer}>
                      <div
                        className={`${tipOptionsList.length !== 1 ? driverTipOptionsContainer : oneTipOptionsNoTipButton}
                       ${tipOptionsList.length === 1 && customTipModeType === CustomTipMode.EDIT ? oneOptionNoTipStyle : ''}
                      `}
                      >
                        {map(tipOptionsList, ({ tipPercentage, tipSelected, tipRoundedAmount }, index) => (
                          <TipOptions
                            index={index}
                            tipSelected={tipSelected}
                            tipPercentage={tipPercentage}
                            className={tipOptionsNoTipButton}
                            tipRoundedAmount={tipRoundedAmount}
                            driverTipOptionSelected={driverTipOptionSelected}
                            isCustomTipSelected={isCustomTipSelected}
                            setIsCustomTipSelected={setIsCustomTipSelected}
                            setDriverTipOptionSelected={setDriverTipOptionSelected}
                            setDriverTipRoundedAmountSelected={setDriverTipRoundedAmountSelected}
                            setShowDriverTipModal={setShowDriverTipModal}
                            isDriverTipPreferenceSet={isDriverTipPreferenceSet}
                            setDriverTipPreferenceSet={setIsDriverTipPreferenceSet}
                            tipOptionsList={tipOptionsList}
                            hasCustomTipError={hasCustomTipError}
                            analyticsEvents={analyticsEvents}
                          />
                        ))}
                      </div>
                      <CustomTip
                        key={customTipKey}
                        customOptionsList={customOptionsList[0]}
                        isCustomTipSelected={isCustomTipSelected}
                        setIsCustomTipSelected={setIsCustomTipSelected}
                        fullWidthCustomTip={true}
                        tabType={'upcomingOrder'}
                        driverTipOptionSelected={driverTipOptionSelected}
                        className={tipOptionsList.length !== 1 ? customTipStyle : oneDriverTipOptionCustomContainer}
                        driverCustomTipValue={driverCustomTipValue}
                        setDriverCustomTipValue={setDriverCustomTipValue}
                        customTipHandler={customTipHandler}
                        orderTotal={get(order, 'estimatedSubTotal', 0)}
                        isUpcomingOrder={true}
                        customTipEditMode={customTipEditMode}
                        setCustomTipEditMode={setCustomTipEditMode}
                        tipOptionsList={tipOptionsList}
                        setCustomTipModeType={setCustomTipModeType}
                        setHasCustomTipError={setHasCustomTipError}
                        analyticsEvents={analyticsEvents}
                      />
                      {showDriverTipModal && (
                        <Modal
                          titleText="Set Your Tip Preferences"
                          titleTextCenter={true}
                          show={showDriverTipModal}
                          fillScreen={true}
                          showBottomTransitionModal={true}
                          showStrokeLines={false}
                          halfHeightMobileModalPerc={'35'}
                          modalOverflowValue="hidden"
                          overFlowBodyHidden={true}
                          onClose={() => {
                            setShowDriverTipModal(false);
                            updateDriverTip(MODAL_CTA.NO, driverTipOptionSelected);
                          }}
                        >
                          <div className={tipPreferenceModalContainer}>
                            <p>{appLabels.PendingOrderContent.driverTipPreferenceModalText}</p>
                            <div className={tipPreferenceButtonContainer}>
                              <button
                                className={notTipSetButton}
                                onClick={() => { 
                                  analyticsEvents('set-driver-pref', {driverTipOptionSelected: driverTipOptionSelected === 'other' ? 'custom-tip' : driverTipOptionSelected === 'notip' ? 'no-tip' : driverTipOptionSelected, option: 'no'});
                                  updateDriverTip(MODAL_CTA.NO, driverTipOptionSelected) 
                                }}
                              >
                                {MODAL_CTA.NO}
                              </button>
                              <button
                                className={tipSetButton}
                                onClick={() => {
                                  analyticsEvents('set-driver-pref', {driverTipOptionSelected: driverTipOptionSelected === 'other' ? 'custom-tip' : driverTipOptionSelected === 'notip' ? 'no-tip' : driverTipOptionSelected, option: 'yes'});
                                  updateDriverTip(MODAL_CTA.YES, driverTipOptionSelected)
                                }}
                              >
                                {MODAL_CTA.YES}
                              </button>
                            </div>
                          </div>
                        </Modal>
                      )}
                      {showTipConfirmationModal && (
                        <Modal
                          titleText={''}
                          titleTextCenter={true}
                          show={showTipConfirmationModal}
                          fillScreen={true}
                          showBottomTransitionModal={true}
                          showStrokeLines={false}
                          halfHeightMobileModalPerc={'35'}
                          modalOverflowValue="hidden"
                          overFlowBodyHidden={true}
                          onClose={handleTipConfirmationModalClose}
                        >
                          <div className={tipConfirmationModalContainer}>
                            <p>{appLabels.PendingOrderContent.customTipConfirmationModalText}</p>
                            <button
                              onClick={() => {
                                setShowTipConfirmationModal(false);
                                updateDriverTip(null, DriverTipType.PREFERENCES_OTHER);
                              }}
                              type="button"
                            >
                              Got it
                            </button>
                          </div>
                        </Modal>
                      )}
                    </div>
                    {(driverTipOptionSelected == '5' ||
                      driverTipOptionSelected == '10' ||
                      driverTipOptionSelected == '15') && (
                      <div className={editDriverTipStyle}>
                        {blueInfoIcon}
                        <p>{appLabels.PreferenceTab.editDriverTipModalInfoText}</p>
                      </div>
                    )}
                  </>
                )}

                <>
                  <div className={strokeStyle} />
                  <div
                    className={`${subTotalStyle} ${svgStyle} ${expanded ? subTotalStyleExpanded : subTotalStyleCollapsed}`}
                  >
                    <div className={`${expanded ? 'body-text' : 'body-text-sm'} label`}>
                      <div className={flexRow}>
                        {appLabels.FutureOrderContent.estimatedTotalBeforeTax}
                        <OrderInfoIcon
                          className={infoIconStyle}
                          width={16}
                          popoverContent={AppLabels.PendingOrderContent.SubtotalInfo}
                          analyticsPageView={''}
                          label={appLabels.FutureOrderContent.estimatedTooltipLabel}
                        />
                      </div>
                    </div>
                    {get(order, 'totalCardSavings') && (
                      <p className={`${expanded ? 'body-text' : 'body-text-sm'} amount original`}>
                        $
                        {calculateFinalTotal(get(order, 'estimatedSubTotal', 0), selectedTipOption, SHOULD_INCLUDE_TIP)}
                      </p>
                    )}
                    <p className={`${expanded ? 'body-text' : `body-text-sm`} amount`}>
                      $
                      {calculateFinalTotal(
                        get(order, 'discountedEstSubTotal', 0),
                        selectedTipOption,
                        SHOULD_INCLUDE_TIP,
                      )}
                    </p>
                  </div>
                </>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={`${subTotalStyle} ${subTotalStyleCollapsed}`}>
              <div className={`${expanded ? 'body-text' : 'body-text-sm'} label`}>
                <div className={flexRow}>
                  {!hideManageLink ? (
                    <button
                      tabIndex={0}
                      aria-label={appLabels.CreateOrderContent.manageLinkText}
                      className={manageLinkStyle}
                      onClick={expandProductList}
                    >
                      {appLabels.CreateOrderContent.manageLinkText}
                    </button>
                  ) : (
                    <div className={manageLinkStyle}></div>
                  )}
                </div>
              </div>

              <OrderInfoIcon
                className={collapesedInfoIconStyle}
                popoverContent={AppLabels.PendingOrderContent.SubtotalInfo}
                analyticsPageView={'pendingOrder'}
                label={AppLabels.PendingOrderContent.estimatedTooltipLabel}
                height={16}
                width={16}
              />
              <div className={flexRow} aria-label={subtotalLabel}>
                <span aria-hidden={true}>{appLabels.PendingOrderContent.estimatedText}</span>
                <p className={`${expanded ? 'body-text' : 'body-text-sm'} amount original`} aria-hidden={true}>
                  ${calculateFinalTotal(get(order, 'estimatedSubTotal', 0), selectedTipOption, SHOULD_INCLUDE_TIP)}
                </p>
                <p
                  className={`${expanded ? 'body-text' : `body-text-sm next-order-amount-padding`} amount`}
                  aria-hidden={true}
                >
                  ${calculateFinalTotal(get(order, 'discountedEstSubTotal', 0), selectedTipOption, SHOULD_INCLUDE_TIP)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
      {isOneTimeAddsAllowed && !expanded && !hideManageLink && (
        <AddOneTimeItems expanded={expanded} upcomingOrders={upcomingOrders} refreshNextOrder={refreshNextOrder} />
      )}
    </div>
  );
};

PendingOrderContent.propTypes = {
  upcomingOrders: PropTypes.object.isRequired,
  callbacks: PropTypes.object,
  orderStatusMessage: PropTypes.string,
  hideManageLink: PropTypes.bool,
  showRescheduleLink: PropTypes.bool,
  onExpand: PropTypes.func,
  hideOrderStatusMessage: PropTypes.bool,
  preferences: PropTypes.object,
};

export default PendingOrderContent;
